/* eslint-disable @typescript-eslint/no-explicit-any */
import IconClose from '@assets/icons/close.svg?react';
import {
 Box,
 Card,
 CardContent,
 IconButton,
 Typography,
 useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import {
 MutableRefObject,
 ReactNode,
 SetStateAction,
 useEffect,
 useMemo,
 useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { useAuthContext } from '../../../contexts/AuthContext';
import { usePage } from '../../../contexts/usePageContext';
import { PageNameType } from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { getBaseDomain } from '../../../redux/pageDomain';
import reduxSlicesList from '../../../redux/reduxSlicesList';
import { RootState } from '../../../redux/store';
import ResultInfoSidebar from '../../results/ResultInfoSidebar';
import { CustomGridApi } from '../XTable/types';
import RoleChecker from '../RoleChecker';
import CommonSidebar from './CommonSidebar';
import UsersSidebar from './UsersSidebar';

const pageNameTypesWithoutResults: PageNameType[] = [
 'customers',
 'vehicles',
 'drivers',
 'products',
 'contracts',
 'destPoint',
 'cards',
 'identity',
];

const InfoSidebar = ({
 apiRef,
 currentId,
 setCurrentId,
}: {
 apiRef: MutableRefObject<CustomGridApi>;
 currentId: number | string | undefined;
 setCurrentId: SetStateAction<any>;
}) => {
 const { pageName } = usePage();
 const { t } = useTranslation();
 const dispatch = useDispatch();
 const loaded = !!currentId;
 const th = useTheme();
 const selector = useSelector(
  (state: RootState) => state[getBaseDomain(pageName)],
 ) as any;
 const { item, success } = selector;
 const { loginUser, databases } = useAuthContext();
 const currentUserDatabase = databases?.find(
  (database) => database.Id === loginUser?.currentlyActiveDb,
 )?.Client;

 const [title, setTitle] = useState<string | ReactNode>('');

 const itemWithId = useMemo(() => {
  return {
   ...item,
   Id: item?.Id || currentId,
  };
 }, [item, currentId]);

 const handleClose = () => {
  setCurrentId(null);
 };

 const getItem = () => {
  if (!currentId) return;

  dispatch(
   reduxSlicesList[pageName as PageNameType]?.getItemStart({
    table: pageName,
    id: currentId,
   }),
  );
 };

 useEffect(() => {
  if (success.updateItem) {
   getItem();
  }
 }, [success.updateItem]);

 useEffect(() => {
  getItem();
 }, [currentId]);

 useEffect(() => {
  if (success.getItem) {
   if (pageName === 'results') {
    setTitle(dayjs(item?.Date).format('YYYY-MM-DD HH:mm'));
   } else if (pageName === 'users') {
    setTitle(
     <Box>
      {item.AuthEmail ? (
       <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
        {item.AuthEmail}
       </Typography>
      ) : null}
      <Typography
       sx={{
        fontSize: 12,
        fontWeight: 500,
        color: (theme) => theme.palette.kemek.gray,
       }}
      >
       {currentUserDatabase}
      </Typography>
     </Box>,
    );
   } else if (itemWithId?.ScalesName ) {
    setTitle(itemWithId?.ScalesName);
   } else if (itemWithId?.FullName) {
    setTitle(itemWithId?.FullName);
   } else if (itemWithId?.Name) {
    setTitle(itemWithId?.Name);
   } else if (itemWithId?.Number) {
    setTitle(itemWithId?.Number);
   } else {
    setTitle(t('global.data'));
   }
  }
 }, [success]);

 return (
  loaded && (
   <Box
    sx={{
     width: loaded ? { xs: '100%', md: '360px' } : '0px',
     transition: 'all 0.2s ease-in-out',
     marginLeft: loaded ? { xs: '0px', md: '16px' } : '0px',
     position: 'relative',
     height: '100%',
     minHeight: 0,
     display: 'flex',
     flexDirection: 'column',
    }}
   >
    <Card
     sx={{
      height: '100%',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
     }}
    >
     <CardContent
      sx={{
       position: 'relative',
       p: 0,
       fontSize: '12px',
       flex: 1,
       minHeight: 0,
       display: 'flex',
       flexDirection: 'column',
       '&:last-child': {
        pb: '0',
       },
      }}
     >
      <Box
       sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: '20px',
        pb: ['results', 'users'].includes(pageName) ? '20px' : '4px',
       }}
      >
       <Box
        sx={{
         fontSize: { xs: 14, md: 16 },
         fontWeight: 500,
         lineheight: 'initial',
        }}
       >
        {title}
       </Box>
       <IconButton
        title={t('global.close')}
        aria-label={t('global.close')}
        onClick={handleClose}
        sx={{
         position: 'relative',
         p: 0,
         top: { xs: 0, md: -8 },
         right: { xs: 0, md: -8 },
        }}
       >
        <IconClose
         style={{ width: 20, height: 20, color: th.palette.kemek.darkestGray }}
        />
       </IconButton>
      </Box>
      <Box
       sx={{
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
       }}
      >
       <RoleChecker resource={pageName} action="Get" showMessage>
        {(() => {
         switch (pageName) {
          case 'results':
           return <ResultInfoSidebar apiRef={apiRef} item={itemWithId} />;
          case 'users':
           return <UsersSidebar item={itemWithId} apiRef={apiRef} />;
          default:
           return (
            <CommonSidebar
             pageName={pageName}
             apiRef={apiRef}
             item={itemWithId}
            />
           );
         }
        })()}
       </RoleChecker>
      </Box>
     </CardContent>
    </Card>
   </Box>
  )
 );
};
export default InfoSidebar;
